import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import ConfirmationMessage from '@/common/components/blocks/Modal/Common/ConfirmationMessage/ConfirmationMessage';
import {
  ENDPOINT_SCANBAT_CHECKOUT_CONFIRM,
  ITEM_TYPES,
  ROLES,
  ROUTE_SCANBAT_CHECKOUT_CONFIRM,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import useOrder from '@/common/hooks/checkout/useOrder';
import SessionStorageScanbat from '@/common/services/SessionStorage.scanbat';

const useCheckout = ({ disabled, isLoading, localCart, warehouse }) => {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();
  const {
    setModalClassName,
    setModalContent,
    setModalIsOpen,
  } = useModalContext();

  const { isDisabled: isDisabledOrder, onAddOrder } = useOrder({
    disabled,
    isLoading,
  });

  const scanbatService = SessionStorageScanbat.getService();

  const isDisabled = useMemo(() => {
    const loading = isLoading || isDisabledOrder.loading;

    return {
      disabled: loading || disabled || isDisabledOrder.disabled,
      loading,
    };
  }, [disabled, isDisabledOrder, isLoading]);

  const initialValues = useMemo(
    () => ({
      email: user?.email || '0',
      email_confirmation: true,
      reference:
        localCart?.reference ||
        localCart?.formatted_number ||
        localCart?.name ||
        '',
      mobile_phone: user?.phone2,
      sms_confirmation: false,
    }),
    [localCart, user],
  );

  const validationSchema = Yup.object({
    reference: Yup.string().matches(
      user?.reference_rules?.regex_format?.length > 0
        ? new RegExp(`^(${user.reference_rules.regex_format.join('|')})$`)
        : '',
      'order.invalid_reference',
    ),
  });

  const onSubmit = useCallback(
    (values, actions) => {
      const {
        email,
        email_confirmation: emailConfirmation,
        new_email: newEmail,
        reference,
        mobile_phone: mobilePhone,
        sms_confirmation: smsConfirmation,
      } = values;

      const checkout = {
        type_object: ITEM_TYPES.cart,
        id_object: localCart.id,
        shipping_type: 'E',
        comment: '',
        email_confirmation: emailConfirmation,
        sms_confirmation: smsConfirmation,
        reference,
        multi_shipping: true,
        store_id: warehouse.code,
        shipping_address: {
          ...warehouse.address,
          country_label: undefined,
        },
        worksite_code:
          user?.role !== ROLES.CUSTOMER_GUEST
            ? localCart.worksite?.code
            : user?.warehouse.code,
        sync: 1,
      };

      if (emailConfirmation) {
        checkout.email = email === '0' ? newEmail : email;
      }

      if (smsConfirmation) {
        checkout.mobile_phone = mobilePhone;
      }

      onAddOrder({
        values: { ...checkout },
        actions,
        onSuccess: (data) => {
          queryClient.setQueryData([ENDPOINT_SCANBAT_CHECKOUT_CONFIRM], data);
          history.push(ROUTE_SCANBAT_CHECKOUT_CONFIRM);
        },
      });
    },
    [history, localCart, onAddOrder, queryClient, user, warehouse],
  );

  const onRemoveWarehouse = useCallback(() => {
    setModalContent(
      <ConfirmationMessage
        description={t('ebat:messages:actions.warehouse.update.description', {
          warehouse: warehouse.name,
        })}
        labelCancel={t('common:messages:actions.cancel')}
        labelSubmit={t('common:messages:actions.logout')}
        onSubmit={() => {
          scanbatService.clearScanBat();
          logout();
        }}
        title={t('ebat:messages:actions.warehouse.update.title')}
      />,
    );
    setModalClassName('modal-confirmation-message');
    setModalIsOpen(true);
  }, [
    logout,
    scanbatService,
    setModalClassName,
    setModalContent,
    setModalIsOpen,
    t,
    warehouse,
  ]);

  return {
    initialValues,
    isDisabled,
    onRemoveWarehouse,
    onSubmit,
    validationSchema,
  };
};

export default useCheckout;
